if (!customElements.get('concierge-hero-slider')) {
  const BM_VISIBLE_PAGINATION_SEGMENTS = 5;

  /**
   * One of N lines active per slide (cycles 0→…→N-1→0…). Avoids bucketing many slides
   * onto one segment, which repeated the fill animation on the same line before advancing.
   */
  function bmActiveSegment(realIndex, slideCount) {
    const w = BM_VISIBLE_PAGINATION_SEGMENTS;
    if (slideCount <= w) {
      return realIndex;
    }
    return ((realIndex % w) + w) % w;
  }

  /** Jump within the current group of slides (same remainder block as Swiper loop). */
  function bmSegmentClickTarget(segmentIndex, slideCount, realIndex) {
    if (slideCount <= 1) return 0;
    const w = BM_VISIBLE_PAGINATION_SEGMENTS;
    if (slideCount <= w) {
      return Math.min(slideCount - 1, segmentIndex);
    }
    const base = realIndex - (realIndex % w);
    return Math.min(slideCount - 1, base + segmentIndex);
  }

  class ConciergeHeroSlider extends HTMLElement {
    connectedCallback() {
      this.swiperEl = this.querySelector('.concierge-hero__swiper');
      if (!this.swiperEl) return;

      const slideCount = this.getSlideCount();
      if (slideCount <= 1) return;

      this.initSlider();
    }

    disconnectedCallback() {
      if (this._bmResizeObserver) {
        this._bmResizeObserver.disconnect();
        this._bmResizeObserver = null;
      }
      if (this._bmOnWindowResize) {
        window.removeEventListener('resize', this._bmOnWindowResize);
        this._bmOnWindowResize = null;
      }
    }

    /** Real slide count from Liquid (Swiper loop duplicates `.swiper-slide` in the DOM). */
    getSlideCount() {
      const fromAttr = parseInt(this.dataset.slideCount, 10);
      if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;
      return this.swiperEl.querySelectorAll('.swiper-slide').length;
    }

    initSlider() {
      const paginationEl = this.querySelector('.concierge-hero__pagination');
      const autoplayDelay = parseInt(this.dataset.autoplay, 10) || 0;
      const spaceBetween = parseInt(this.dataset.spaceBetween, 10) || 0;
      const slideCount = this.getSlideCount();
      const isBmHero = Boolean(this.closest('.bm-concierge-hero'));
      this.bmCappedSegmentPagination = isBmHero && slideCount > BM_VISIBLE_PAGINATION_SEGMENTS;

      const options = {
        slidesPerView: 'auto',
        centeredSlides: true,
        spaceBetween,
        loop: true,
        grabCursor: true,
        threshold: 2,
        loopAdditionalSlides: 5,
        ...(isBmHero
          ? {
              centeredSlidesBounds: true,
              observer: true,
              observeParents: true,
            }
          : {}),
        pagination:
          paginationEl && !this.bmCappedSegmentPagination
            ? { el: paginationEl, clickable: true }
            : false,
        on: {
          init: (swiper) => {
            if (this.bmCappedSegmentPagination) {
              this.initBmSlidingPagination(swiper, paginationEl, slideCount, autoplayDelay);
            }
            requestAnimationFrame(() => {
              requestAnimationFrame(() => swiper.update());
            });
          },
          slideChange: () => {
            if (this.bmCappedSegmentPagination) {
              this.updateBmSlidingPagination();
            }
          },
          slideChangeTransitionEnd(swiper) {
            requestAnimationFrame(() => swiper.update());
          },
        },
      };

      const modules = [];

      if (autoplayDelay > 0) {
        options.autoplay = {
          delay: autoplayDelay,
          disableOnInteraction: false,
        };
        modules.push(FoxTheme.Swiper.Autoplay);
      }

      this.carousel = new FoxTheme.Carousel(this.swiperEl, options, modules);
      this.carousel.init();

      const swiper = this.carousel.slider;
      if (isBmHero && swiper) {
        this._bmScheduleSwiperUpdate = () => {
          requestAnimationFrame(() => {
            if (!this.carousel?.slider) return;
            const s = this.carousel.slider;
            s.update();
            if (typeof s.loopFix === 'function') {
              s.loopFix();
            }
          });
        };

        this._bmResizeObserver = new ResizeObserver(() => this._bmScheduleSwiperUpdate());
        this._bmResizeObserver.observe(this.swiperEl);

        this._bmOnWindowResize = () => this._bmScheduleSwiperUpdate();
        window.addEventListener('resize', this._bmOnWindowResize);

        setTimeout(() => this._bmScheduleSwiperUpdate(), 150);
      }
    }

    initBmSlidingPagination(swiper, container, slideCount, autoplayDelayMs) {
      this.bmPaginationContainer = container;
      this.bmSlideCount = slideCount;
      if (autoplayDelayMs > 0) {
        container.style.setProperty('--bm-concierge-hero-pagination-fill', `${autoplayDelayMs}ms`);
      }

      container.innerHTML = '';
      container.classList.add('concierge-hero__pagination--bm-capped');
      this.bmPrevRealIndex = null;
      this.bmPrevActiveSlot = null;

      this.bmPaginationButtons = [];
      for (let i = 0; i < BM_VISIBLE_PAGINATION_SEGMENTS; i += 1) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'swiper-pagination-bullet';
        btn.setAttribute('aria-label', `Slide ${i + 1} of ${BM_VISIBLE_PAGINATION_SEGMENTS} in view`);
        btn.addEventListener('click', () => {
          swiper.slideToLoop(bmSegmentClickTarget(i, slideCount, swiper.realIndex));
        });
        container.appendChild(btn);
        this.bmPaginationButtons.push(btn);
      }

      this.bmSwiperRef = swiper;
      this.updateBmSlidingPagination();
    }

    updateBmSlidingPagination() {
      const swiper = this.bmSwiperRef;
      const container = this.bmPaginationContainer;
      if (!swiper || !container || !this.bmPaginationButtons?.length) return;

      const idx = swiper.realIndex;
      const activeSlot = bmActiveSegment(idx, this.bmSlideCount);
      const sameLineNewSlide =
        this.bmPrevRealIndex !== null &&
        this.bmPrevRealIndex !== idx &&
        this.bmPrevActiveSlot === activeSlot;

      this.bmPaginationButtons.forEach((btn, i) => {
        const isActive = i === activeSlot;
        btn.classList.toggle('swiper-pagination-bullet-active', isActive);
        const target = bmSegmentClickTarget(i, this.bmSlideCount, idx);
        btn.setAttribute('aria-label', `Go to slide ${target + 1} of ${this.bmSlideCount}`);
        if (isActive) {
          btn.setAttribute('aria-current', 'true');
        } else {
          btn.removeAttribute('aria-current');
        }
      });

      if (sameLineNewSlide) {
        this.restartBmActiveBulletFill(container);
      }

      this.bmPrevRealIndex = idx;
      this.bmPrevActiveSlot = activeSlot;
    }

    restartBmActiveBulletFill(container) {
      container.classList.add('concierge-hero__pagination--restart-fill');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          container.classList.remove('concierge-hero__pagination--restart-fill');
        });
      });
    }
  }

  customElements.define('concierge-hero-slider', ConciergeHeroSlider);
}
